import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { HTTPError, post } from "common/helpers/HTTP";
import { parseLeadsSearchParams } from "common/helpers/utils";
import type {
  CrmType,
  EmailOutreachType,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import type { OUTREACH_CRM_EXPORT_OPTIONS } from "../constants";
import type { CrmExportSummary } from "../types";

async function exportLeadsRequest(
  workspaceId: string,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<string> {
  const { url } = await post(
    `workspaces/${workspaceId}/leads-export`,
    parseLeadsSearchParams(searchParams),
  );
  return url;
}

async function exportLeadsToCrmIntegrationRequest(
  workspaceId: string,
  crmType: CrmType,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
): Promise<string | undefined> {
  const { job_id } = await post(
    `workspaces/${workspaceId}/integrations/${crmType}/leads-export`,
    parseLeadsSearchParams(searchParams),
  );
  return job_id;
}

async function exportLeadsToOutreachCrmRequest(
  workspaceId: string,
  crmType: EmailOutreachType,
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  type: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS,
  id: string,
): Promise<CrmExportSummary> {
  return (
    await post(
      `workspaces/${workspaceId}/integrations/${crmType}/leads-export`,
      { ...parseLeadsSearchParams(searchParams), [`${type}_id`]: id },
    )
  ).export;
}

export default function useLeadExports() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { mutateAsync: exportLeads, isPending: isExportingLeads } = useMutation(
    {
      mutationFn: ({
        searchParams,
      }: {
        searchParams: LeadsSearchParams<SelectedFacetParam>;
      }) => exportLeadsRequest(workspaceId, searchParams),
    },
  );

  const {
    mutateAsync: exportLeadsToCrmIntegration,
    isPending: isExportingLeadsToCrmIntegration,
  } = useMutation({
    mutationFn: ({
      searchParams,
      crmType,
    }: {
      searchParams: LeadsSearchParams<SelectedFacetParam>;
      crmType: CrmType;
    }) =>
      exportLeadsToCrmIntegrationRequest(workspaceId, crmType, searchParams),
  });

  const {
    mutateAsync: exportLeadsToOutreachCrm,
    isPending: isExportingLeadsToOutreachCrm,
  } = useMutation({
    mutationFn: ({
      type,
      searchParams,
      sourceType,
      id,
    }: {
      type: EmailOutreachType;
      searchParams: LeadsSearchParams<SelectedFacetParam>;
      sourceType: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS;
      id: string;
    }) =>
      exportLeadsToOutreachCrmRequest(
        workspaceId,
        type,
        searchParams,
        sourceType,
        id,
      ),
    onSuccess: (_, { type }) => {
      const label = `${type.charAt(0).toUpperCase()}${type.slice(1)}`;

      toast.success(`Successfully exported leads to ${label}`);
    },
    onError: (error, { type }) => {
      const label = `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
      let errorMessage = `Failed to export leads to ${label}`;

      if (error instanceof HTTPError) {
        if (error.type === "Unauthorized") {
          errorMessage = `${label} API token expired. Update your API key to resolve this`;
        } else if (error.type === "Too Many Requests") {
          errorMessage = `${label} rate limit hit. Please try again later`;
        } else if (error.type === "Bad Request") {
          errorMessage =
            "Export failed: No valid leads found. Make sure uploaded leads have an email address";
        }
      }

      toast.error(errorMessage);
    },
  });

  return {
    exportLeads,
    isExportingLeads,
    exportLeadsToCrmIntegration,
    isExportingLeadsToCrmIntegration,
    exportLeadsToOutreachCrm,
    isExportingLeadsToOutreachCrm,
  };
}
